/**
 * UUID → human label resolver.
 *
 * Plan cards, run-step inspectors and tool-result blocks render whatever
 * the agent / engine produced — which ends up containing raw UUID strings
 * for instruments, approach versions, etc. This module batches lookups
 * via `/api/labels?ids=…` and exposes a stable React hook plus a small
 * helper that walks an arbitrary JSON-shaped value and substitutes any
 * UUID-shaped string with `name (kind, abc12345)`.
 */
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Walk an arbitrary value and collect every UUID-shaped string. */
export function collectUuids(value, out = new Set()) {
    if (typeof value === "string") {
        if (UUID_RE.test(value))
            out.add(value.toLowerCase());
        return out;
    }
    if (Array.isArray(value)) {
        for (const v of value)
            collectUuids(v, out);
        return out;
    }
    if (value && typeof value === "object") {
        for (const v of Object.values(value))
            collectUuids(v, out);
    }
    return out;
}
/** Render a UUID via the label map. Falls back to a short prefix when
 *  the ID isn't a known entity. */
export function renderId(id, labels) {
    const norm = id.toLowerCase();
    const hit = labels?.[norm];
    if (hit)
        return `${hit.label} · ${hit.kind}`;
    return `${id.slice(0, 8)}…`;
}
/** Recursively replace UUID strings with `name (kind, prefix)` substitutes,
 *  preserving structure. Handy for `JSON.stringify` previews. */
export function substituteIds(value, labels) {
    if (typeof value === "string") {
        return UUID_RE.test(value) ? renderId(value, labels) : value;
    }
    if (Array.isArray(value)) {
        return value.map((v) => substituteIds(v, labels));
    }
    if (value && typeof value === "object") {
        const out = {};
        for (const [k, v] of Object.entries(value)) {
            out[k] = substituteIds(v, labels);
        }
        return out;
    }
    return value;
}
/** Hook that fetches labels for every UUID found in `seed`. Returns an
 *  empty map while loading so callers can render a fallback prefix. */
export function useEntityLabels(seed) {
    const ids = Array.from(collectUuids(seed));
    ids.sort();
    const cacheKey = ids.join(",");
    const q = useQuery({
        queryKey: ["entity-labels", cacheKey],
        enabled: ids.length > 0,
        staleTime: 5 * 60 * 1000,
        queryFn: () => api.get(`/labels?ids=${encodeURIComponent(cacheKey)}`),
    });
    return q.data?.labels ?? {};
}
